import moment from 'moment';
import { PackageType } from '../types/PackageType';

export interface SpaTimeBand {
  startTime: string;
  endTime: string;
}

interface TimedSelection {
  start_date?: string;
  start_time?: string;
  end_time?: string;
}

const at = (date: string, time: string) =>
  moment(`${date} ${time}`, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'], true);

const toSeconds = (time: string) => moment.duration(time).asSeconds();

export const timeBandDays = (packageType: PackageType, stayDuration: number) =>
  packageType === PackageType.REPEATING ? stayDuration + 1 : stayDuration;

export const timeBandWindow = (
  bandsByDate: Record<string, SpaTimeBand[] | undefined> | undefined
): SpaTimeBand | undefined => {
  const bands = Object.values(bandsByDate || {}).flatMap((dateBands) => dateBands || []);
  if (bands.length === 0) return undefined;

  return bands.reduce((window, band) => ({
    startTime:
      toSeconds(band.startTime) < toSeconds(window.startTime) ? band.startTime : window.startTime,
    endTime: toSeconds(band.endTime) > toSeconds(window.endTime) ? band.endTime : window.endTime,
  }));
};

const isTimed = (selection: TimedSelection): selection is Required<TimedSelection> =>
  !!selection.start_date && !!selection.start_time && !!selection.end_time;

const holds = (
  selections: TimedSelection[],
  opensOn: (date: string) => moment.Moment,
  closesOn: (date: string) => moment.Moment
) =>
  selections
    .filter(isTimed)
    .every(
      (selection) =>
        !at(selection.start_date, selection.start_time).isBefore(opensOn(selection.start_date)) &&
        !at(selection.start_date, selection.end_time).isAfter(closesOn(selection.start_date))
    );

export const pickTimeBand = (
  bands: SpaTimeBand[],
  checkInDate: string,
  checkOutDate: string,
  selections: TimedSelection[]
): SpaTimeBand | undefined =>
  bands.find((band) =>
    holds(
      selections,
      (date) => at(date, band.startTime),
      (date) => at(date, band.endTime)
    )
  ) ??
  bands.find((band) =>
    holds(
      selections,
      () => at(checkInDate, band.startTime),
      () => at(checkOutDate, band.endTime)
    )
  );
